<template>
  <b-modal 
    id="windy-weather-modal" 
    size="lg" 
    :title="modalTitle"
    hide-footer
    @hidden="onModalHidden"
  >
    <!-- Состояние загрузки -->
    <div v-if="isLoading" class="text-center p-4">
      <b-spinner class="mr-2"></b-spinner>
      <span>Loading weather data...</span>
    </div>

    <!-- Состояние ошибки -->
    <div v-else-if="errorMessage" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle mr-2"></i>
      {{ errorMessage }}
    </div>

    <!-- Отображение данных о погоде -->
    <div v-else-if="weatherData" class="weather-container">
      <!-- Сводка текущей погоды -->
      <div v-if="currentWeather" class="current-weather mb-4">
        <!-- Три крупных блока в одну линию -->
        <div class="row">
          <div class="col-md-4">
            <div class="weather-card">
              <div class="weather-icon">
                <i class="fas fa-thermometer-half text-danger"></i>
              </div>
              <div class="weather-data">
                <div class="value">{{ currentWeather.temp_2m }}°C</div>
                <div class="label">Temperature</div>
              </div>
            </div>
          </div>
          
          <div class="col-md-4">
            <div class="weather-card">
              <div class="weather-icon">
                <i class="fas fa-wind text-primary"></i>
              </div>
              <div class="weather-data">
                <div class="value">{{ (currentWeather.wind_speed_10m || 0).toFixed(1) }} m/s</div>
                <div class="label">Wind Speed</div>
                <div class="sub-label">{{ getWindDirection(currentWeather.wind_v_10m, currentWeather.wind_u_10m) }}°</div>
              </div>
            </div>
          </div>
          
          <div class="col-md-4" v-if="currentWeather.waves_height && currentWeather.waves_height > 0">
            <div class="weather-card">
              <div class="weather-icon">
                <i class="fas fa-water text-blue"></i>
              </div>
              <div class="weather-data">
                <div class="value">{{ (currentWeather.waves_height || 0).toFixed(1) }} m</div>
                <div class="label">Wave Height</div>
                <div class="sub-label">{{ Math.round(currentWeather.waves_direction || 0) }}° direction</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Мелкие метрики в одну строку -->
        <div class="row mt-3">
          <div class="col-md-3">
            <div class="weather-metric">
              <i class="fas fa-tint text-info mr-2"></i>
              Humidity: {{ Math.round(currentWeather.rh_2m || 0) }}%
            </div>
          </div>
          
          <div class="col-md-3">
            <div class="weather-metric">
              <i class="fas fa-gauge-high text-warning mr-2"></i>
              Pressure: {{ Math.round(currentWeather.surface_pressure || 0) }} hPa
            </div>
          </div>
          
          <div class="col-md-3">
            <div class="weather-metric">
              <i class="fas fa-cloud text-secondary mr-2"></i>
              Cloudiness: {{ Math.round(currentWeather.clouds || 0) }}%
            </div>
          </div>
          
          <div class="col-md-3" v-if="currentWeather.rain_3h > 0">
            <div class="weather-metric">
              <i class="fas fa-cloud-rain text-blue mr-2"></i>
              Rain (3h): {{ currentWeather.rain_3h.toFixed(1) }} mm
            </div>
          </div>
        </div>

        <!-- Снег отдельно если есть -->
        <div v-if="currentWeather.snow_3h > 0" class="row mt-3">
          <div class="col-md-12">
            <div class="weather-metric">
              <i class="fas fa-snowflake text-light-blue mr-2"></i>
              Snow (3h): {{ currentWeather.snow_3h.toFixed(1) }} mm
            </div>
          </div>
        </div>
      </div>

      <!-- Вкладки прогноза -->
      <b-tabs card class="weather-tabs">
        <!-- Почасовой прогноз -->
        <b-tab title="24h Forecast" active>
          <div class="forecast-container">
            <div class="forecast-scroll">
              <div 
                v-for="(hour, index) in hourlyForecast" 
                :key="index" 
                class="hourly-item"
                :class="{ 'current-hour': index === 0 }"
              >
                <div class="hour-time">{{ formatHourTime(hour.time) }}</div>
                <div class="hour-temp">{{ hour.temp_2m }}°C</div>
                <div class="hour-wind">
                  <i class="fas fa-wind text-primary"></i>
                  {{ (hour.wind_speed_10m || 0).toFixed(1) }} m/s
                </div>
                <div class="hour-precip" v-if="hour.rain_1h > 0">
                  <i class="fas fa-cloud-rain text-blue"></i>
                  {{ hour.rain_1h.toFixed(1) }}mm
                </div>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Ежедневный прогноз -->
        <b-tab title="7 Day Forecast">
          <div class="daily-forecast">
            <div 
              v-for="(day, index) in dailyForecast" 
              :key="index" 
              class="daily-item"
            >
              <div class="day-name">{{ formatDayName(day.date) }}</div>
              <div class="day-temps">
                <span class="temp-max">{{ day.temp_max }}°</span>
                <span class="temp-min text-muted">{{ day.temp_min }}°</span>
              </div>
              <div class="day-wind">
                <i class="fas fa-wind text-primary"></i>
                {{ day.wind_avg }} m/s
              </div>
              <div class="day-precip" v-if="day.rain_sum > 0">
                <i class="fas fa-cloud-rain text-blue"></i>
                {{ day.rain_sum }}mm
              </div>
            </div>
          </div>
        </b-tab>

        <!-- График ветра -->
        <b-tab title="Wind Chart">
          <div class="chart-container">
            <canvas ref="windChart" style="height: 300px;"></canvas>
          </div>
        </b-tab>

        <!-- График температуры -->
        <b-tab title="Temperature Chart">
          <div class="chart-container">
            <canvas ref="tempChart" style="height: 300px;"></canvas>
          </div>
        </b-tab>
      </b-tabs>
    </div>
  </b-modal>
</template>

<script>
import Chart from 'chart.js/auto'
import dayjs from 'dayjs'

export default {
  name: 'WindyWeatherModal',
  data() {
    return {
      isLoading: false,
      errorMessage: null,
      weatherData: null,
      coordinates: { lat: 0, lng: 0 },
      requestTime: null,
      windChart: null,
      tempChart: null
    }
  },
  computed: {
    modalTitle() {
      const coords = `${this.coordinates.lat.toFixed(3)}°, ${this.coordinates.lng.toFixed(3)}°`
      const dateTime = this.requestTime ? dayjs.unix(this.requestTime).format('DD MMM YYYY, HH:mm') : ''
      return `Weather: 💧 ${coords} 📅 ${dateTime}`
    },
    formattedDateTime() {
      if (!this.requestTime) return ''
      return dayjs.unix(this.requestTime).format('DD MMM YYYY, HH:mm')
    },
    currentWeather() {
      if (!this.weatherData?.ts || this.weatherData.ts.length === 0) return null
      
      // Берем первый элемент временного ряда как текущую погоду
      const currentIndex = 0
      const current = {}
      
      // Извлекаем данные для текущего времени из всех метрик
      Object.keys(this.weatherData).forEach(key => {
        if (key !== 'ts' && Array.isArray(this.weatherData[key])) {
          current[key] = this.weatherData[key][currentIndex]
        }
      })
      
      return current
    },
    hourlyForecast() {
      if (!this.weatherData?.ts) return []
      
      // Берем первые 24 часа прогноза для почасового отображения
      return this.weatherData.ts.slice(0, 24).map((timestamp, index) => {
        const forecast = { time: timestamp }
        
        Object.keys(this.weatherData).forEach(key => {
          if (key !== 'ts' && Array.isArray(this.weatherData[key])) {
            forecast[key] = this.weatherData[key][index] || 0
          }
        })
        
        return forecast
      })
    },
    dailyForecast() {
      if (!this.weatherData?.ts) return []
      
      // Используем все доступные данные (168 часов) для группировки по дням
      const allHours = this.weatherData.ts.map((timestamp, index) => {
        const forecast = { time: timestamp }
        
        Object.keys(this.weatherData).forEach(key => {
          if (key !== 'ts' && Array.isArray(this.weatherData[key])) {
            forecast[key] = this.weatherData[key][index] || 0
          }
        })
        
        return forecast
      })
      
      // Группируем почасовые данные по дням
      const days = {}
      
      allHours.forEach(hour => {
        const dayKey = dayjs.unix(hour.time).format('YYYY-MM-DD')
        
        if (!days[dayKey]) {
          days[dayKey] = {
            date: hour.time,
            temps: [],
            winds: [],
            rains: []
          }
        }
        
        days[dayKey].temps.push(hour.temp_2m || 0)
        days[dayKey].winds.push(hour.wind_speed_10m || 0)
        days[dayKey].rains.push(hour.rain_1h || 0)
      })
      
      // Вычисляем агрегированные значения для каждого дня
      return Object.keys(days).slice(0, 7).map(dayKey => {
        const dayData = days[dayKey]
        return {
          date: dayData.date,
          temp_max: Math.max(...dayData.temps),
          temp_min: Math.min(...dayData.temps),
          wind_avg: (dayData.winds.reduce((a, b) => a + b, 0) / dayData.winds.length).toFixed(1),
          rain_sum: Math.round(dayData.rains.reduce((a, b) => a + b, 0) * 10) / 10
        }
      })
    }
  },
  methods: {
    show(coordinates, time, weatherData) {
      this.coordinates = coordinates
      this.requestTime = time
      this.weatherData = weatherData
      this.errorMessage = null
      
      this.$bvModal.show('windy-weather-modal')
      
      // Рендерим графики после показа модального окна
      this.$nextTick(() => {
        setTimeout(() => {
          this.renderCharts()
        }, 300) // Даем время табам загрузиться
      })
    },
    
    showLoading(coordinates, time) {
      this.coordinates = coordinates
      this.requestTime = time
      this.isLoading = true
      this.weatherData = null
      this.errorMessage = null
      
      this.$bvModal.show('windy-weather-modal')
    },
    
    showError(error, coordinates, time) {
      this.coordinates = coordinates
      this.requestTime = time
      this.isLoading = false
      this.weatherData = null
      this.errorMessage = error
      
      this.$bvModal.show('windy-weather-modal')
    },
    
    updateData(weatherData) {
      this.isLoading = false
      this.weatherData = weatherData
      this.errorMessage = null
      
      this.$nextTick(() => {
        setTimeout(() => {
          this.renderCharts()
        }, 300)
      })
    },
    
    onModalHidden() {
      // Очищаем графики при закрытии модала
      if (this.windChart) {
        this.windChart.destroy()
        this.windChart = null
      }
      if (this.tempChart) {
        this.tempChart.destroy()
        this.tempChart = null
      }
    },
    
    getWindDirection(v, u) {
      if (!v || !u) return 0
      let angle = Math.atan2(v, u) * 180 / Math.PI
      if (angle < 0) angle += 360
      return Math.round(angle)
    },
    
    formatHourTime(timestamp) {
      return dayjs.unix(timestamp).format('HH:mm')
    },
    
    formatDayName(timestamp) {
      const day = dayjs.unix(timestamp)
      const today = dayjs()
      
      if (day.isSame(today, 'day')) return 'Today'
      if (day.isSame(today.add(1, 'day'), 'day')) return 'Tomorrow'
      
      return day.format('ddd DD')
    },
    
    renderCharts() {
      this.renderWindChart()
      this.renderTempChart()
    },
    
    renderWindChart() {
      if (this.windChart) {
        this.windChart.destroy()
      }
      
      if (!this.$refs.windChart || !this.hourlyForecast.length) return
      
      const ctx = this.$refs.windChart.getContext('2d')
      const labels = this.hourlyForecast.map(h => this.formatHourTime(h.time))
      const windSpeeds = this.hourlyForecast.map(h => (h.wind_speed_10m || 0).toFixed(1))
      const windDirections = this.hourlyForecast.map(h => 
        this.getWindDirection(h.wind_v_10m, h.wind_u_10m)
      )
      
      this.windChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            label: 'Wind Speed (m/s)',
            data: windSpeeds,
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'rgba(54, 162, 235, 0.1)',
            tension: 0.4,
            yAxisID: 'y'
          }, {
            label: 'Wind Direction (°)',
            data: windDirections,
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(255, 99, 132, 0.1)',
            tension: 0.4,
            yAxisID: 'y1'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: 'Time'
              }
            },
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: {
                display: true,
                text: 'Wind Speed (m/s)'
              }
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              title: {
                display: true,
                text: 'Wind Direction (°)'
              },
              grid: {
                drawOnChartArea: false,
              },
            }
          }
        }
      })
    },
    
    renderTempChart() {
      if (this.tempChart) {
        this.tempChart.destroy()
      }
      
      if (!this.$refs.tempChart || !this.hourlyForecast.length) return
      
      const ctx = this.$refs.tempChart.getContext('2d')
      const labels = this.hourlyForecast.map(h => this.formatHourTime(h.time))
      const temperatures = this.hourlyForecast.map(h => h.temp_2m || 0)
      const humidity = this.hourlyForecast.map(h => h.rh_2m || 0)
      
      this.tempChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            label: 'Temperature (°C)',
            data: temperatures,
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(255, 99, 132, 0.1)',
            tension: 0.4,
            yAxisID: 'y'
          }, {
            label: 'Humidity (%)',
            data: humidity,
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'rgba(54, 162, 235, 0.1)',
            tension: 0.4,
            yAxisID: 'y1'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: 'Time'
              }
            },
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: {
                display: true,
                text: 'Temperature (°C)'
              }
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              title: {
                display: true,
                text: 'Humidity (%)'
              },
              grid: {
                drawOnChartArea: false,
              },
            }
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.weather-container {
  max-height: 80vh;
  overflow-y: auto;
}

.current-weather {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
}

.weather-card {
  display: flex;
  align-items: center;
  padding: 0.8rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  height: 100%;
  min-height: 80px;
}

.weather-icon {
  font-size: 1.8rem;
  margin-right: 0.8rem;
  flex-shrink: 0;
}

.weather-data .value {
  font-size: 1.2rem;
  font-weight: 500;
  color: #495057;
}

.weather-data .label {
  font-size: 0.9rem;
  color: #6c757d;
}

.weather-data .sub-label {
  font-size: 0.8rem;
  color: #6c757d;
}

.weather-metric {
  padding: 0.6rem;
  background: white;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  color: #495057;
  font-size: 0.85rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.forecast-container {
  max-height: 400px;
  overflow-y: auto;
}

.forecast-scroll {
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  overflow-x: auto;
  min-height: 120px;
}

.hourly-item {
  min-width: 80px;
  text-align: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 2px solid transparent;
}

.hourly-item.current-hour {
  background: #e3f2fd;
  border-color: #2196F3;
}

.hour-time {
  font-weight: 500;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  color: #495057;
}

.hour-temp {
  color: #d32f2f;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.hour-wind {
  color: #1976d2;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.hour-precip {
  color: #0277bd;
  font-size: 0.8rem;
}

.daily-forecast {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.daily-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
}

.day-name {
  font-weight: 500;
  min-width: 80px;
  color: #495057;
}

.day-temps {
  min-width: 80px;
  text-align: center;
}

.temp-max {
  font-weight: 500;
  margin-right: 0.5rem;
  color: #495057;
}

.temp-min {
  font-size: 0.9rem;
}

.day-wind, .day-precip {
  min-width: 80px;
  text-align: center;
  font-size: 0.9rem;
}

.chart-container {
  padding: 1rem;
  height: 350px;
}

:deep(.weather-tabs .nav-tabs) {
  border-bottom: 2px solid #dee2e6;
}

:deep(.weather-tabs .nav-link) {
  color: #6c757d;
  font-weight: 500;
}

:deep(.weather-tabs .nav-link.active) {
  color: #495057;
  border-bottom: 2px solid #007bff;
}

.text-blue {
  color: #0277bd !important;
}

.text-light-blue {
  color: #4fc3f7 !important;
}
</style>